import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, History, Loader2, RefreshCw, Undo2, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { createDuplicateContactsApi } from '../../lib/duplicateContactsApi';
import { getContactHistory } from '../../lib/historyApi';
import { actorDetails, historyItems, historyKindLabel, historyOperationId, historyTimestamp } from '../../lib/historyDisplay';
import { errorText } from '../../lib/apiErrors';
import CustomFieldBadge from '../../components/custom-fields/CustomFieldBadge';

const operationId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `merge-${Date.now()}`);

function contactLabel(contact) {
    return [contact?.first_name, contact?.last_name].filter(Boolean).join(' ') || contact?.email || contact?.contact_id;
}

// Server instants carry their UTC offset; show them in the viewer's own zone.
function formatWhen(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

const FIELD_NAMES = {
    first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone', mobile: 'Mobile',
    title: 'Title', company: 'Company', website: 'Website', linkedin_url: 'LinkedIn', address: 'Address',
    city: 'City', state: 'State', postal_code: 'Postal code', country: 'Country', owner_id: 'Contact Owner',
    account_id: 'Company record', source: 'Source', notes: 'Notes',
};
const EVENT_FIELD_NAMES = {
    rsvp_status: 'RSVP', attendance_status: 'Attendance', outreach_stage: 'Outreach stage',
    next_follow_up_at: 'Next follow-up', last_outreach_at: 'Last outreach',
    workflow_id: 'Workflow', workflow_version_id: 'Workflow version',
};
const CUSTOM_PREFIX = 'custom_fields.';
const isCustomField = (field) => typeof field === 'string' && field.startsWith(CUSTOM_PREFIX);
// A custom field reads by its definition's label (the key when the definition is archived or unknown).
const fieldName = (field, labels = {}) => FIELD_NAMES[field] || labels[field] || (isCustomField(field) ? field.slice(CUSTOM_PREFIX.length) : field);
const eventFieldName = (field) => EVENT_FIELD_NAMES[field] || field;

/** The field's name for the page, with the Custom badge on a custom field. */
function FieldLabel({ field, labels }) {
    return (
        <>
            {fieldName(field, labels)}
            {isCustomField(field) && <CustomFieldBadge className="ml-1.5" />}
        </>
    );
}

function shortText(value) {
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return text.length > 40 ? `${text.slice(0, 39)}…` : text;
}

/** A contact field's value as the operator knows it: owner names, not ids. */
function fieldValue(field, value, ownerNames) {
    if (value === null || value === undefined || value === '') return 'none';
    if (field === 'owner_id') return ownerNames[value] || 'Former member';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return shortText(value.join(', '));
    return shortText(value);
}

/** An event dimension's value: "to_invite" reads "To invite", dates in the viewer's zone. */
function eventValue(field, value) {
    if (value === null || value === undefined || value === '') return 'none';
    if (field.endsWith('_at')) return formatWhen(value) || shortText(value);
    const text = String(value).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function ownerLabel(contact) {
    if (contact?.owner_name) return contact.owner_name;
    // An owner id with no name is no longer a member of this org (#861 F1).
    return contact?.owner_id ? 'Former member' : 'Unassigned';
}

function mergeStatusText(item) {
    if (item.status === 'undone') return `Undone ${formatWhen(item.undone_at)}${item.undone_by?.name ? ` by ${item.undone_by.name}` : ''}`;
    if (item.status === 'undoing') return 'Undo was interrupted — run Undo again to finish it';
    if (item.undoable) return `Undo until ${formatWhen(item.undo_expires_at)}`;
    return 'Undo window closed';
}

/** Inline history of one (possibly merged-away) contact, from the contact history API. */
function ContactHistoryPeek({ contactId, getAuthHeader }) {
    const [state, setState] = useState({ loading: true, items: [], error: null });
    useEffect(() => {
        let alive = true;
        getContactHistory(contactId, { limit: 20 }, getAuthHeader ? getAuthHeader() : {})
            .then((payload) => { if (alive) setState({ loading: false, items: historyItems(payload), error: null }); })
            .catch((err) => { if (alive) setState({ loading: false, items: [], error: errorText(err, 'Could not load this contact\'s history') }); });
        return () => { alive = false; };
    }, [contactId, getAuthHeader]);
    if (state.loading) return <p className="mt-2 text-xs text-stone-500">Loading history…</p>;
    if (state.error) return <p className="mt-2 text-xs text-red-700">{state.error}</p>;
    if (!state.items.length) return <p className="mt-2 text-xs text-stone-500">No history recorded.</p>;
    return (
        <ul className="mt-2 space-y-1 border-l border-stone-200 pl-3" data-testid={`contact-history-${contactId}`}>
            {state.items.map((item) => (
                <li key={item.id || `${historyKindLabel(item)}:${historyTimestamp(item)}`} className="text-xs text-stone-600">
                    <span className="font-medium text-stone-800">{historyKindLabel(item)}</span>
                    {' · '}{actorDetails(item)}{' · '}{formatWhen(historyTimestamp(item)) || historyTimestamp(item)}
                    {historyOperationId(item) && <span className="text-stone-400"> · {historyOperationId(item)}</span>}
                </li>
            ))}
        </ul>
    );
}

export default function AdminDuplicateContactsPage() {
    const { getAuthHeader } = useAuth();
    const { permissionsUnknown, hasEffectivePermission } = usePermissions();
    const api = useMemo(() => createDuplicateContactsApi(getAuthHeader), [getAuthHeader]);
    const [groups, setGroups] = useState([]);
    const [selectedKey, setSelectedKey] = useState('');
    const [survivorId, setSurvivorId] = useState('');
    const [fieldChoices, setFieldChoices] = useState({});
    const [eventChoices, setEventChoices] = useState({});
    const [preview, setPreview] = useState(null);
    // The last merge's result. Deliberately NOT reset when the selected group
    // changes: a merge removes its group, the refresh selects the next one, and
    // the confirmation with its Undo button must survive that (#910 QA).
    const [lastMerge, setLastMerge] = useState(null);
    const [recentMerges, setRecentMerges] = useState([]);
    const [recentError, setRecentError] = useState(null);
    const [openHistory, setOpenHistory] = useState('');
    const [notice, setNotice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [undoingId, setUndoingId] = useState('');
    const [error, setError] = useState(null);

    const canMerge = !permissionsUnknown && hasEffectivePermission('contacts:merge');
    const selectedGroup = groups.find((group) => group.email_key === selectedKey) || null;

    const loadGroups = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await api.list();
            const nextGroups = result?.groups || [];
            setGroups(nextGroups);
            setSelectedKey((current) => (nextGroups.some((group) => group.email_key === current) ? current : (nextGroups[0]?.email_key || '')));
        } catch (err) {
            setError(errorText(err, 'Could not load duplicate contacts'));
        } finally {
            setLoading(false);
        }
    }, [api]);

    const loadRecent = useCallback(async () => {
        if (!canMerge) return;
        try {
            const result = await api.recentMerges();
            setRecentMerges(result?.operations || []);
            setRecentError(null);
        } catch (err) {
            setRecentError(errorText(err, 'Could not load recent merges'));
        }
    }, [api, canMerge]);

    useEffect(() => { loadGroups(); }, [loadGroups]);
    useEffect(() => { loadRecent(); }, [loadRecent]);
    useEffect(() => {
        setPreview(null);
        setFieldChoices({});
        setEventChoices({});
        setSurvivorId(selectedGroup?.suggested_survivor_id || selectedGroup?.contacts?.[0]?.contact_id || '');
    }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const loserIds = (selectedGroup?.contacts || []).map((contact) => contact.contact_id).filter((id) => id !== survivorId);
    const ownerNames = Object.fromEntries((selectedGroup?.contacts || [])
        .filter((contact) => contact.owner_id).map((contact) => [contact.owner_id, ownerLabel(contact)]));
    const eventNames = Object.fromEntries([...(preview?.event_conflicts || []), ...(preview?.event_fills || [])]
        .map((item) => [item.event_id, item.event_name || 'an event']));
    // "event:<id>:<field>" from the server, as the operator reads it.
    const missingEventLabel = (key) => {
        const [, eventId, field] = String(key).split(':');
        return `${eventNames[eventId] || 'an event'} · ${eventFieldName(field)}`;
    };
    const chooseField = (field, value) => {
        const next = { ...fieldChoices, [field]: value };
        setFieldChoices(next);
        runPreview({ field: next });
    };
    const chooseEvent = (eventId, field, value) => {
        const next = { ...eventChoices, [eventId]: { ...(eventChoices[eventId] || {}), [field]: value } };
        setEventChoices(next);
        runPreview({ event: next });
    };
    const filledEntries = Object.entries(preview?.filled_fields || {});
    // Custom fields name themselves by label: the server sends it with each custom conflict and carried value.
    const customLabels = {
        ...(preview?.custom_labels || {}),
        ...Object.fromEntries((preview?.conflicts || []).filter((item) => item.label).map((item) => [item.field, item.label])),
    };
    const eventFillEntries = (preview?.event_fills || []).flatMap((fill) => Object.entries(fill.fields || {})
        .map(([field, value]) => ({ key: `${fill.event_id}:${field}`, eventName: fill.event_name || 'An event', field, value })));
    // A choice is part of the preview (its hash covers the choices), so changing one re-runs it:
    // Merge then reflects exactly what the operator picked, with no second Preview click (#910).
    const runPreview = async ({ field = fieldChoices, event = eventChoices } = {}) => {
        if (!selectedGroup || !survivorId || !loserIds.length) return;
        setWorking(true);
        setError(null);
        try {
            setPreview(await api.preview({ survivor_id: survivorId, loser_ids: loserIds, field_choices: field, event_choices: event }));
        } catch (err) {
            setError(errorText(err, 'Could not preview merge'));
        } finally {
            setWorking(false);
        }
    };

    const commitMerge = async () => {
        if (!preview || !canMerge || preview.missing_choices?.length || preview.missing_event_choices?.length) return;
        const survivor = selectedGroup?.contacts?.find((contact) => contact.contact_id === survivorId);
        const losers = (selectedGroup?.contacts || []).filter((contact) => contact.contact_id !== survivorId);
        setWorking(true);
        setError(null);
        setNotice(null);
        try {
            const result = await api.merge({
                operation_id: operationId(),
                survivor_id: survivorId,
                loser_ids: loserIds,
                preview_hash: preview.preview_hash,
                field_choices: fieldChoices,
                event_choices: eventChoices,
                reason: 'Duplicate contact review',
            });
            setLastMerge({ ...result, survivor_label: contactLabel(survivor), loser_labels: losers.map(contactLabel) });
            await Promise.all([loadGroups(), loadRecent()]);
        } catch (err) {
            setError(errorText(err, 'Merge failed; refresh and review the conflicts'));
        } finally {
            setWorking(false);
        }
    };

    const undoMerge = async (id) => {
        if (!id || !canMerge) return;
        setUndoingId(id);
        setError(null);
        setNotice(null);
        try {
            const result = await api.undo(id);
            setLastMerge((current) => (current?.operation_id === id ? { ...current, ...result, status: 'undone' } : current));
            setNotice('Merge undone. Both contacts are back in Contacts and their duplicate group is back in the list.');
            await Promise.all([loadGroups(), loadRecent()]);
        } catch (err) {
            // undo_conflict / undo_expired explain themselves; later edits are never overwritten.
            setError(errorText(err, 'Undo failed; later edits were not overwritten'));
            await loadRecent();
        } finally {
            setUndoingId('');
        }
    };

    const lastMergeUndoable = lastMerge?.status === 'completed'
        && (!lastMerge.undo_expires_at || new Date(lastMerge.undo_expires_at).getTime() > Date.now());

    return (
        <AdminLayout title="Duplicate contacts" description="Review normalized-email duplicates before merging records.">
            {permissionsUnknown && <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><AlertTriangle className="h-4 w-4" />Permissions are still loading. Merge actions remain hidden until the server confirms access.</div>}
            {!permissionsUnknown && !canMerge && <div className="mb-4 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-600"><AlertTriangle className="h-4 w-4" />You have read-only access because the <code>contacts:merge</code> permission is required to review or merge duplicate records.</div>}
            {error && <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert"><span>{error}</span><button aria-label="Reload duplicate groups" onClick={() => { loadGroups(); loadRecent(); }}><RefreshCw className="h-4 w-4" /></button></div>}
            {notice && <div className="mb-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" role="status" data-testid="undo-notice"><span><CheckCircle2 className="mr-2 inline h-4 w-4" />{notice}</span><button aria-label="Dismiss" onClick={() => setNotice(null)}><X className="h-4 w-4" /></button></div>}
            {lastMerge && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" role="status" data-testid="merge-result">
                    <div>
                        <p className="font-medium">
                            <CheckCircle2 className="mr-2 inline h-4 w-4" />
                            {lastMerge.status === 'undone'
                                ? `Merge undone: ${[lastMerge.survivor_label, ...(lastMerge.loser_labels || [])].filter(Boolean).join(' and ')} are separate contacts again.`
                                : `Merged ${(lastMerge.loser_labels || []).join(', ') || 'the duplicate'} into ${lastMerge.survivor_label || lastMerge.survivor_id}.`}
                        </p>
                        <p className="mt-1 text-xs text-emerald-700">
                            Operation {lastMerge.operation_id}
                            {lastMerge.status !== 'undone' && (lastMergeUndoable
                                ? ` · Undo until ${formatWhen(lastMerge.undo_expires_at) || 'the undo window closes'}`
                                : ' · Undo window closed')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canMerge && lastMergeUndoable && <button className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100" onClick={() => undoMerge(lastMerge.operation_id)} disabled={Boolean(undoingId)} data-action="contact.merge_undo"><Undo2 className="h-3 w-3" />{undoingId === lastMerge.operation_id ? 'Undoing…' : 'Undo'}</button>}
                        <button aria-label="Dismiss merge result" onClick={() => setLastMerge(null)}><X className="h-4 w-4" /></button>
                    </div>
                </div>
            )}
            {loading ? <div className="flex items-center gap-2 p-6 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" />Loading duplicate groups…</div> : (
                <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
                    <aside className="rounded-lg border border-stone-200 bg-white">
                        <div className="border-b border-stone-200 px-4 py-3 text-sm font-semibold">Duplicate groups ({groups.length})</div>
                        {groups.length === 0 ? <p className="p-4 text-sm text-stone-500">No duplicate email groups need review.</p> : <div className="divide-y divide-stone-100">{groups.map((group) => <button key={group.email_key} className={`block w-full px-4 py-3 text-left ${selectedKey === group.email_key ? 'bg-blue-50' : 'hover:bg-stone-50'}`} onClick={() => setSelectedKey(group.email_key)}><p className="truncate text-sm font-medium text-stone-800">{group.email_key}</p><p className="mt-1 text-xs text-stone-500">{group.count} contacts</p></button>)}</div>}
                    </aside>
                    <main className="space-y-4">
                        {!selectedGroup ? <div className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500">Select a duplicate group to compare records.</div> : <>
                            <div className="grid gap-3 md:grid-cols-2">
                                {selectedGroup.contacts.map((contact) => <label key={contact.contact_id} className={`rounded-lg border p-4 ${survivorId === contact.contact_id ? 'border-blue-400 bg-blue-50' : 'border-stone-200 bg-white'}`}><div className="flex items-start gap-3"><input type="radio" name="survivor" checked={survivorId === contact.contact_id} onChange={() => { setSurvivorId(contact.contact_id); setPreview(null); }} /><div><p className="font-semibold text-stone-900">{contactLabel(contact)}</p><p className="text-xs text-stone-500">{contact.email || contact.email_key || contact.contact_id}</p><p className="mt-2 text-xs text-stone-500">Owner: {ownerLabel(contact)}</p></div></div></label>)}
                            </div>
                            {preview?.conflicts?.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><p className="font-medium text-amber-900">Resolve field conflicts</p><div className="mt-3 space-y-2">{preview.conflicts.map((conflict) => { const optional = conflict.requires_choice === false; return <label className="flex items-center justify-between gap-3 text-sm" key={conflict.field}><span><FieldLabel field={conflict.field} labels={customLabels} /></span><select aria-label={`${fieldName(conflict.field, customLabels)} to keep`} className="rounded border border-amber-300 bg-white px-2 py-1" value={fieldChoices[conflict.field] || (optional ? conflict.default_choice || 'survivor' : '')} onChange={(event) => chooseField(conflict.field, event.target.value)} disabled={working}>{!optional && <option value="" disabled>Choose…</option>}<option value="survivor">Keep survivor: {fieldValue(conflict.field, conflict.survivor_value, ownerNames)}</option><option value="loser">Use duplicate: {fieldValue(conflict.field, conflict.loser_values?.find((value) => value !== null && value !== undefined && value !== ''), ownerNames)}</option></select></label>; })}</div></div>}
                            {preview?.event_conflicts?.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><p className="font-medium text-amber-900">Resolve event state conflicts</p><div className="mt-3 space-y-2">{preview.event_conflicts.map((conflict) => conflict.fields.map((field) => <label className="flex items-center justify-between gap-3 text-sm" key={`${conflict.event_id}:${field}`}><span>{conflict.event_name || 'An event'} · {eventFieldName(field)}</span><select aria-label={`${conflict.event_name || 'Event'} ${eventFieldName(field)} to keep`} className="rounded border border-amber-300 bg-white px-2 py-1" value={eventChoices[conflict.event_id]?.[field] || ''} onChange={(event) => chooseEvent(conflict.event_id, field, event.target.value)} disabled={working}><option value="" disabled>Choose…</option><option value="survivor">Keep survivor: {eventValue(field, conflict.survivor_state?.[field])}</option><option value="loser">Use duplicate: {eventValue(field, conflict.loser_state?.[field])}</option></select></label>))}</div></div>}
                            {preview && <div className="rounded-lg border border-stone-200 bg-white p-4 text-sm"><p className="font-medium">Merge preview</p><p className="mt-1 text-stone-600">{preview.relation_counts ? Object.values(preview.relation_counts).reduce((sum, count) => sum + count, 0) : 0} relationship records will point to the survivor.</p>{(filledEntries.length > 0 || eventFillEntries.length > 0) && <div className="mt-2 text-stone-600" data-testid="merge-kept-from-duplicate"><p>Kept from the duplicate, because the survivor has none:</p><ul className="mt-1 list-disc pl-5">{filledEntries.map(([field, value]) => <li key={field}><FieldLabel field={field} labels={customLabels} />: {fieldValue(field, value, ownerNames)}</li>)}{eventFillEntries.map((item) => <li key={item.key}>{item.eventName} · {eventFieldName(item.field)}: {eventValue(item.field, item.value)}</li>)}</ul></div>}{preview.missing_choices?.length > 0 && <p className="mt-2 text-amber-700">Choose which value to keep for: {preview.missing_choices.map((field) => fieldName(field, customLabels)).join(', ')}</p>}{preview.missing_event_choices?.length > 0 && <p className="mt-2 text-amber-700">Choose which value to keep for: {preview.missing_event_choices.map(missingEventLabel).join(', ')}</p>}</div>}
                            <div className="flex flex-wrap gap-2"><button className="rounded border border-stone-300 bg-white px-4 py-2 text-sm" onClick={() => runPreview()} disabled={working || loserIds.length === 0}>{working ? 'Preparing…' : 'Preview merge'}</button>{canMerge && <button className="rounded bg-blue-700 px-4 py-2 text-sm text-white" onClick={commitMerge} disabled={working || !preview || preview.missing_choices?.length > 0 || preview.missing_event_choices?.length > 0}>{working ? 'Merging…' : 'Merge contacts'}</button>}</div>
                        </>}
                    </main>
                </div>
            )}
            {canMerge && (
                <section className="mt-6 rounded-lg border border-stone-200 bg-white" data-testid="recent-merges">
                    <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
                        <p className="text-sm font-semibold">Recent merges</p>
                        <button className="text-stone-500 hover:text-stone-700" aria-label="Reload recent merges" onClick={loadRecent}><RefreshCw className="h-4 w-4" /></button>
                    </div>
                    {recentError && <p className="px-4 py-3 text-sm text-red-700">{recentError}</p>}
                    {!recentError && recentMerges.length === 0 && <p className="px-4 py-3 text-sm text-stone-500">No merges yet.</p>}
                    <ul className="divide-y divide-stone-100">
                        {recentMerges.map((item) => (
                            <li key={item.operation_id} className="px-4 py-3 text-sm" data-testid={`recent-merge-${item.operation_id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-stone-800">
                                            {(item.losers || []).map((loser, index) => (
                                                <React.Fragment key={loser.contact_id}>
                                                    {index > 0 && ', '}
                                                    <Link className="font-medium underline-offset-2 hover:underline" to={`/contacts/${encodeURIComponent(loser.contact_id)}`}>{loser.name}</Link>
                                                    {loser.is_deleted && loser.merged_into_contact_id === item.survivor_id && <span className="text-xs text-stone-500"> (merged away)</span>}
                                                </React.Fragment>
                                            ))}
                                            {' → '}
                                            <Link className="font-medium underline-offset-2 hover:underline" to={`/contacts/${encodeURIComponent(item.survivor_id)}`}>{item.survivor?.name || item.survivor_id}</Link>
                                        </p>
                                        <p className="mt-1 text-xs text-stone-500">
                                            {item.actor?.name || item.actor?.id || 'Unknown'} · {formatWhen(item.completed_at || item.created_at)} · {mergeStatusText(item)}
                                        </p>
                                        <p className="mt-1 text-xs text-stone-400">Operation {item.operation_id}</p>
                                        {/* Each contact's OWN history, survivor included: the merge is one entry in each,
                                            under the same operation id (#910). */}
                                        <div className="mt-1 flex flex-wrap gap-3">
                                            {[...(item.losers || []), { contact_id: item.survivor_id, name: item.survivor?.name || item.survivor_id }].map((person) => (
                                                <button key={person.contact_id} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900" onClick={() => setOpenHistory((current) => (current === `${item.operation_id}:${person.contact_id}` ? '' : `${item.operation_id}:${person.contact_id}`))}>
                                                    <History className="h-3 w-3" />{openHistory === `${item.operation_id}:${person.contact_id}` ? 'Hide' : 'Show'} history of {person.name}
                                                </button>
                                            ))}
                                        </div>
                                        {[...(item.losers || []).map((loser) => loser.contact_id), item.survivor_id].filter((contactId) => openHistory === `${item.operation_id}:${contactId}`).map((contactId) => (
                                            <ContactHistoryPeek key={contactId} contactId={contactId} getAuthHeader={getAuthHeader} />
                                        ))}
                                    </div>
                                    {item.undoable && <button className="inline-flex shrink-0 items-center gap-1 rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50" onClick={() => undoMerge(item.operation_id)} disabled={Boolean(undoingId)} data-action="contact.merge_undo"><Undo2 className="h-3 w-3" />{undoingId === item.operation_id ? 'Undoing…' : 'Undo'}</button>}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </AdminLayout>
    );
}
